"use client";

import { useEffect, useRef } from "react";
import { useBackendHealth } from "@/hooks/useBackendHealth";
import { AlertTriangle, CheckCircle2, RefreshCw, WifiOff } from "lucide-react";

/**
 * A thin persistent banner that renders at the top of the page when the
 * backend API is down or degraded.  When healthy it is invisible (zero height).
 * When the backend recovers it briefly flashes a green "Reconnected" bar.
 */
export function BackendStatusBanner() {
  const { status, latencyMs, hint, recovered, checkNow } = useBackendHealth();
  const prevStatusRef = useRef<string>("checking");

  useEffect(() => {
    const playBeep = (frequency: number, durationMs: number) => {
      try {
        const AudioContextImpl =
          window.AudioContext ||
          (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioContextImpl) return;
        const context = new AudioContextImpl();
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = "sine";
        oscillator.frequency.value = frequency;
        gain.gain.value = 0.035;
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start();
        window.setTimeout(() => {
          oscillator.stop();
          void context.close();
        }, durationMs);
      } catch {
        // Audio may be blocked until user interaction; skip silently.
      }
    };

    const previous = prevStatusRef.current;
    if (status === "down" && previous !== "down") {
      // Distinct lower alert tone when backend goes down.
      playBeep(460, 120);
    } else if (
      (status === "ok" || status === "degraded") &&
      (previous === "down" || previous === "checking")
    ) {
      // Brighter tone when backend comes back.
      playBeep(720, 90);
    }
    prevStatusRef.current = status;
  }, [status]);

  // Healthy and no recent recovery -- render nothing
  if (status === "ok" && !recovered) return null;
  if (status === "checking") return null;

  // Recovery toast
  if (recovered && (status === "ok" || status === "degraded")) {
    return (
      <div className="sticky top-0 z-[9999] flex items-center justify-center gap-2 bg-emerald-600 px-4 py-2 text-xs font-medium text-white shadow-lg animate-in fade-in slide-in-from-top-2 duration-300">
        <CheckCircle2 className="h-4 w-4" />
        <span>Backend reconnected{latencyMs ? ` (${latencyMs}ms)` : ""}</span>
      </div>
    );
  }

  if (status === "degraded") {
    return (
      <div className="sticky top-0 z-[9999] flex items-center justify-center gap-2 bg-amber-600 px-4 py-2 text-xs font-medium text-white shadow-lg">
        <AlertTriangle className="h-4 w-4" />
        <span>Backend service degraded -- some features may be slow or unavailable.</span>
        <button
          onClick={checkNow}
          className="ml-2 inline-flex items-center gap-1 rounded-md border border-amber-400/50 bg-amber-700/60 px-2 py-0.5 text-[11px] hover:bg-amber-700 transition"
        >
          <RefreshCw className="h-3 w-3" /> Retry
        </button>
      </div>
    );
  }

  // status === "down"
  return (
    <div className="sticky top-0 z-[9999] flex flex-col items-center gap-1 bg-rose-700 px-4 py-3 text-white shadow-lg">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <WifiOff className="h-4 w-4" />
        <span>Backend API is unreachable</span>
      </div>
      <p className="max-w-xl text-center text-xs text-rose-200">
        {hint || "The server may be starting up. Retrying automatically every few seconds."}
      </p>
      <button
        onClick={checkNow}
        className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-rose-400/40 bg-rose-800/60 px-3 py-1 text-xs font-medium hover:bg-rose-800 transition"
      >
        <RefreshCw className="h-3 w-3" /> Check now
      </button>
    </div>
  );
}
